import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Table, Badge, ProgressBar } from 'react-bootstrap';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import DatePeriodNavigation, { PeriodChangeEvent } from '../components/common/DatePeriodNavigation';
import { getUsers, getAnalyticsPerformance } from '../api/generated/arrayscapeGamingDashboardAPI';

interface TeamMember {
  id: string;
  name: string;
  role: string;
  territory: string;
  quota: number;
  sales: number;
  commission: number;
  deals: number;
}

const TeamDashboard: React.FC = () => {
  const [currentPeriod, setCurrentPeriod] = useState('Current Month');
  const [teamData, setTeamData] = useState<TeamMember[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);

  const fetchTeamData = async (startDate: string, endDate: string) => {
    setTeamLoading(true);
    try {
      // Get list of team members (salespeople)
      const usersResponse = await getUsers({ role: 'salesperson', sort_field: 'first_name', sort_direction: 'asc' });
      const teamMembers = usersResponse.data || [];

      // Fetch performance data for each team member
      const teamPerformancePromises = teamMembers.map(async (user: any) => {
        try {
          const perfResponse = await getAnalyticsPerformance({
            user_id: user.id,
            start_date: startDate,
            end_date: endDate
          });

          return {
            id: user.id,
            name: `${user.first_name} ${user.last_name}`,
            role: user.role === 'salesperson' ? 'Sales Rep' : 'Senior Sales Rep',
            territory: user.territory || 'Unknown',
            quota: perfResponse.quota_amount || 0,
            sales: perfResponse.total_sales || 0,
            commission: perfResponse.total_commission || 0,
            deals: perfResponse.deals_closed || 0,
          };
        } catch (error) {
          console.error(`Failed to fetch performance for user ${user.id}:`, error);
          return {
            id: user.id,
            name: `${user.first_name} ${user.last_name}`,
            role: user.role === 'salesperson' ? 'Sales Rep' : 'Senior Sales Rep',
            territory: user.territory || 'Unknown',
            quota: 0,
            sales: 0,
            commission: 0,
            deals: 0,
          };
        }
      });

      const teamPerformanceData = await Promise.all(teamPerformancePromises);
      setTeamData(teamPerformanceData);
    } catch (error) {
      console.error('Failed to fetch team data:', error);
      // Fall back to mock data on error
      setTeamData(mockTeamData);
    } finally {
      setTeamLoading(false);
    }
  };

  useEffect(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const startDate = startOfMonth.toISOString().split('T')[0];
    const endDate = endOfMonth.toISOString().split('T')[0];

    fetchTeamData(startDate, endDate);
  }, []);

  const handlePeriodChange = (event: PeriodChangeEvent) => {
    // Fetch updated team data for the new period
    fetchTeamData(event.dateRange.start_date, event.dateRange.end_date);

    setCurrentPeriod(event.period);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Team data will be fetched from API - placeholder for now
  const mockTeamData = [
    {
      id: 'team-001',
      name: 'John Smith',
      role: 'Senior Sales Rep',
      quota: 100000,
      sales: 85000,
      commission: 8500,
      deals: 12,
      territory: 'Northeast'
    },
    {
      id: 'team-002',
      name: 'Sarah Johnson',
      role: 'Sales Rep',
      quota: 80000,
      sales: 92000,
      commission: 11500,
      deals: 15,
      territory: 'Southeast'
    },
    {
      id: 'team-003',
      name: 'Mike Wilson',
      role: 'Sales Rep',
      quota: 75000,
      sales: 62000,
      commission: 6200,
      deals: 8,
      territory: 'West'
    },
    {
      id: 'team-004',
      name: 'Lisa Chen',
      role: 'Senior Sales Rep',
      quota: 90000,
      sales: 105000,
      commission: 13250,
      deals: 18,
      territory: 'Midwest'
    },
  ];

  const getAttainmentBadge = (attainment: number) => {
    if (attainment >= 1.0) return <Badge bg="success">Exceeded</Badge>;
    if (attainment >= 0.8) return <Badge bg="warning">On Track</Badge>;
    return <Badge bg="danger">Below Target</Badge>;
  };

  const stickyHeaderContent = (
    <>
      <DatePeriodNavigation 
        onPeriodChange={handlePeriodChange}
      />
      <Row className="mb-3">
        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100">
            <Card.Body className="text-center">
              <div className="mb-3">
                <i className="bi bi-people fs-1 text-primary"></i>
              </div>
              <h3 className="mb-1">{teamLoading ? '...' : teamData.length}</h3>
              <p className="text-muted mb-0">Team Members</p>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100">
            <Card.Body className="text-center">
              <div className="mb-3">
                <i className="bi bi-currency-dollar fs-1 text-success"></i>
              </div>
              <h3 className="mb-1">{teamLoading ? '...' : formatCurrency(teamData.reduce((sum, member) => sum + member.sales, 0))}</h3>
              <p className="text-muted mb-0">Total Sales</p>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100">
            <Card.Body className="text-center">
              <div className="mb-3">
                <i className="bi bi-award fs-1 text-warning"></i>
              </div>
              <h3 className="mb-1">{teamLoading ? '...' : formatCurrency(teamData.reduce((sum, member) => sum + member.commission, 0))}</h3>
              <p className="text-muted mb-0">Total Commission</p>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100">
            <Card.Body className="text-center">
              <div className="mb-3">
                <i className="bi bi-briefcase fs-1 text-info"></i>
              </div>
              <h3 className="mb-1">{teamLoading ? '...' : teamData.reduce((sum, member) => sum + member.deals, 0)}</h3>
              <p className="text-muted mb-0">Total Deals</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  return (
    <DashboardLayout
      title="Team Dashboard"
      subtitle={`Overview of team performance and commission tracking - ${currentPeriod}`}
      stickyHeader={stickyHeaderContent}
      scrollable
    >
      <>
        <Row>
        <Col>
          <Card className="">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <i className="bi bi-people me-2"></i>
                Team Performance Overview
              </h5>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Sales Rep</th>
                    <th>Territory</th>
                    <th>Quota</th>
                    <th>Sales</th>
                    <th>Attainment</th>
                    <th>Commission</th>
                    <th>Deals</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {teamLoading ? (
                    <tr>
                      <td colSpan={8} className="text-center py-4">
                        <div className="d-flex justify-content-center align-items-center">
                          <div className="spinner-border text-primary me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Loading team data...
                        </div>
                      </td>
                    </tr>
                  ) : teamData.map((member) => {
                    const attainment = member.sales / member.quota;
                    return (
                      <tr key={member.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="avatar-circle bg-primary text-white me-3 d-flex align-items-center justify-content-center" 
                                 style={{ width: '40px', height: '40px', borderRadius: '50%', fontSize: '14px', fontWeight: 'bold' }}>
                              {member.name.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div>
                              <div className="fw-semibold">{member.name}</div>
                              <small className="text-muted">{member.role}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <Badge bg="light" text="dark">{member.territory}</Badge>
                        </td>
                        <td>{formatCurrency(member.quota)}</td>
                        <td className="fw-semibold">{formatCurrency(member.sales)}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="flex-grow-1 me-2">
                              <ProgressBar
                                now={attainment * 100}
                                variant={attainment >= 1.0 ? 'success' : attainment >= 0.8 ? 'warning' : 'danger'}
                                style={{ height: '8px' }}
                              />
                            </div>
                            <small className="fw-semibold">
                              {(attainment * 100).toFixed(0)}%
                            </small>
                          </div>
                        </td>
                        <td className="fw-bold text-primary">{formatCurrency(member.commission)}</td>
                        <td>
                          <Badge bg="info">{member.deals}</Badge>
                        </td>
                        <td>{getAttainmentBadge(attainment)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={6}>
          <Card className="">
            <Card.Header className="bg-success text-white">
              <h6 className="mb-0">
                <i className="bi bi-trophy me-2"></i>
                Top Performers
              </h6>
            </Card.Header>
            <Card.Body>
              {teamLoading ? (
                <div className="text-center">
                  <div className="spinner-border text-success" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : teamData
                .sort((a, b) => (b.sales / b.quota) - (a.sales / a.quota))
                .slice(0, 3)
                .map((member, index) => (
                  <div key={member.id} className={`d-flex align-items-center ${index < 2 ? 'mb-3' : ''}`}>
                    <div className="me-3">
                      {index === 0 && <i className="bi bi-award-fill text-warning fs-4"></i>}
                      {index === 1 && <i className="bi bi-award text-secondary fs-4"></i>}
                      {index === 2 && <i className="bi bi-award text-warning fs-4" style={{ opacity: 0.6 }}></i>}
                    </div>
                    <div className="flex-grow-1">
                      <div className="fw-semibold">{member.name}</div>
                      <small className="text-muted">
                        {((member.sales / member.quota) * 100).toFixed(0)}% of quota
                      </small>
                    </div>
                    <div className="text-end">
                      <div className="fw-bold">{formatCurrency(member.sales)}</div>
                    </div>
                  </div>
                ))}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="">
            <Card.Header className="bg-info text-white">
              <h6 className="mb-0">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Needs Attention
              </h6>
            </Card.Header>
            <Card.Body>
              {teamLoading ? (
                <div className="text-center">
                  <div className="spinner-border text-info" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : teamData
                .filter(member => (member.sales / member.quota) < 0.8)
                .map((member, index) => (
                  <div key={member.id} className={`d-flex align-items-center ${index < 1 ? 'mb-3' : ''}`}>
                    <div className="me-3">
                      <i className="bi bi-exclamation-circle text-warning fs-4"></i>
                    </div>
                    <div className="flex-grow-1">
                      <div className="fw-semibold">{member.name}</div>
                      <small className="text-muted">
                        {((member.sales / member.quota) * 100).toFixed(0)}% of quota - 
                        Needs {formatCurrency(member.quota - member.sales)} more
                      </small>
                    </div>
                    <div className="text-end">
                      <Badge bg="warning">Action Needed</Badge>
                    </div>
                  </div>
                ))}
              
              {!teamLoading && teamData.filter(member => (member.sales / member.quota) < 0.8).length === 0 && (
                <div className="text-center text-muted">
                  <i className="bi bi-check-circle fs-3 text-success mb-2 d-block"></i>
                  All team members are on track!
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      </>
    </DashboardLayout>
  );
};

export default TeamDashboard;